import { PortableTextProps } from '@portabletext/react'
import { FieldHints } from '../../atoms/at-text-field/shared/field-hints'
import { AtButton, Cancel, WarningCircle } from '../../atoms'
import { AtButtonDimensions, AtButtonTypes, AtButtonBehaviors } from '../../types'
import { baseFieldClasses } from '../../atoms/at-text-field/shared/classes'
import { useState, FormEvent } from 'react'

export interface MlDiscountFieldProps {
  placeholder?: string
  errorText?: string | PortableTextProps['value']
  hasError?: boolean
  name: string
  title: string
  value?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onReset?: () => void
  onCouponApplied?: (coupon: string) => Promise<boolean>
  onCouponRemove?: () => void
}

export const MlDiscountField = ({
  errorText,
  hasError,
  name,
  placeholder,
  title,
  value,
  onChange,
  onReset,
  onCouponApplied,
  onCouponRemove,
}: MlDiscountFieldProps) => {
  const [coupon, setCoupon] = useState(value)
  const warnIconColor = hasError ? 'text-feedback-negative-300' : 'text-neutrals-800'

  const handleSubmit = async (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault()

    if (!value || coupon) return

    if (onCouponApplied && (await onCouponApplied(value))) {
      setCoupon(value)
    } else {
      onReset && onReset()
    }
  }

  const handleRemoveCoupon = () => {
    setCoupon('')
    onCouponRemove && onCouponRemove()
  }
  return (
    <>
      <div className="lg:flex text-16 font-sans font-regular">
        <div className="lg:flex lg:flex-col lg:w-full">
          <div className="grow relative text-neutrals-600 focus-within:text-neutrals-800 mb-2">
            <input
              className={`h-14 py-2 pl-4 ${baseFieldClasses(hasError)} ${hasError ? 'pr-20' : 'pr-12'}`}
              id={`${name}-field`}
              name={name}
              placeholder={placeholder}
              type="text"
              value={coupon ? '' : value}
              onChange={onChange}
              disabled={coupon !== '' && true}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e)
                }
              }}
            />
            {hasError && (
              <WarningCircle
                className={`pointer-events-none absolute bottom-4 ${value ? 'right-12' : 'right-4'} ${warnIconColor}`}
              />
            )}
          </div>
          {hasError && (
            <div className="mb-2">
              <FieldHints errorText={errorText} hasError={hasError} />
            </div>
          )}
          {!hasError && coupon && (
            <div className={`inline-flex items-center text-16 gap-4 px-4 py-2 bg-neutrals-100 h-12 mb-4 lg:w-fit`}>
              <div className="text-neutrals-800">
                <span className="text-black">{coupon}</span>
              </div>
              <button
                aria-label="Remove discount code"
                className={`items-center justify-center`}
                onClick={handleRemoveCoupon}
                type={AtButtonBehaviors.BUTTON}
              >
                <Cancel />
              </button>
            </div>
          )}
        </div>
        <div className="lg:ml-4">
          <AtButton
            title={title}
            className="px-4 lg:px-0 w-full lg:w-[9.3125rem]"
            type={AtButtonTypes.SECONDARY}
            size={AtButtonDimensions.LARGE}
            typeButton={AtButtonBehaviors.BUTTON}
            onClick={handleSubmit}
            parentName="MlDiscountField"
          />
        </div>
      </div>
    </>
  )
}
